/**
 * Pure decision helpers for the "Record a voice note for this slide?" prompt
 * shown once per slide when a pathology case opens (hook: `use-voice-note-prompt.tsx`).
 */
import type { VoiceNoteRecorderStatus } from "@frontend/pages/case-workspace/pathology/hooks/use-voice-note-recorder"

/** How long the prompt stays on screen before fading with no answer. */
export const VOICE_NOTE_PROMPT_DURATION_MS = 15_000
export const VOICE_NOTE_PROMPT_TOAST_ID = "voice-note-prompt"

export interface VoiceNotePromptConditions {
  caseId: string | undefined
  activeSeriesId: string | null
  /** Own in-progress submission, not review / view-only / bootstrapping, module present. */
  canRecord: boolean
  /** The viewer has loaded the active slide (the recorder needs its level-0 size). */
  isViewerReady: boolean
  status: VoiceNoteRecorderStatus
  /** Non-null when this browser cannot record WebM/Opus. */
  unsupportedReason: string | null
}

/** One prompt per slide per case visit. */
export function voiceNotePromptKey(caseId: string, seriesId: string): string {
  return `${caseId}:${seriesId}`
}

/**
 * The prompt is only worth showing when pressing Start would actually begin a
 * recording: rights + slide + viewer are there, the recorder is idle (no
 * recording, no failed upload waiting on Retry) and the browser can record.
 */
export function shouldPromptVoiceNote(c: VoiceNotePromptConditions): c is VoiceNotePromptConditions & {
  caseId: string
  activeSeriesId: string
} {
  return Boolean(
    c.caseId && c.activeSeriesId && c.canRecord && c.isViewerReady && c.status === "idle" && !c.unsupportedReason
  )
}
